<script lang="ts">
  /**
   * A page of lists: an optional header, free blocks on top, a search, and
   * sections of rows, each a label, a group and a note. TwinTen's Friends tab
   * layout (and Interlock's You / My scores).
   *
   * ## The insets are fixed
   *
   * Every section is laid out the same way, so no page can drift: the label
   * (the eyebrow step, `SectionLabel`) and the note under the group share
   * one small inset from the column's edge; the group itself runs to the
   * column's edge (the page's gutter); the blocks are a fixed gap apart. A
   * section's `content` is its rows (`FriendRow`s, `Row`s): it draws no card
   * of its own around them, so there is never a card in a card.
   *
   * - `title`: a header with the page's name (`ScreenHeader` when there is
   *   an `onback` for its chevron, a centred title otherwise). Absent (a tab
   *   inside `AppFrame`, whose bar already names it): no header.
   * - `lead`: blocks above everything (a note that the list is from before
   *   going offline, an error). Invites to a game are not a list's: they
   *   are Home's (`HomeTemplate`'s `invites`, since 1.2.0).
   * - `search`: the search block (`SearchSuggest`, with its error under it),
   *   first, or after the section named by `searchAfter` when that section
   *   is there ("Requests" above the search, the rest under it).
   * - `sections`: in order.
   *
   * ## Where it is drawn (`frame`, since 1.3.0)
   *
   * - `screen` (the default, as in 1.2.0): in place, a tab's content or one
   *   of the game's own routed screens. `safeArea` pads the titled header's
   *   top by the notch, for a page that is the first thing on the screen.
   * - `page`: a `FullPage` (as `PickerPage`'s `page` frame), the native
   *   modal page over the column, in the `done` dress unless `dress` says
   *   otherwise. It needs `title` and `onback`: Done (or Back, or the
   *   chevron), Escape and the Back gesture all call `onback`, and the page
   *   owns its history entry. `open` (default `true`) and `wide` are
   *   `FullPage`'s. The props stay as loose as 1.2.0's, so a page without
   *   either is drawn in place, with a console warning; `ListPagePageProps`
   *   (`menuTypes.ts`) is the strict shape for a game that wants the check
   *   at compile time.
   *
   * Nothing here fetches. Test ids: `testid` on the page (default
   * `ui-list-page`; the dialog in the `page` frame); `testids.back` on
   * the way back (ScreenHeader's `ui-screen-back`, or FullPage's
   * `ui-page-back`) and `testids.done` on Done (FullPage's `ui-page-done`);
   * each section's `testid` on its group.
   */
  import type { Snippet } from 'svelte';

  import FullPage from '../components/FullPage.svelte';
  import ScreenHeader from '../components/ScreenHeader.svelte';
  import SectionLabel from '../components/SectionLabel.svelte';
  import type { AnyListSection } from './menuTypes.js';
  import type { PageDress, PageFrame as Frame } from './pageTypes.js';

  interface Props {
    /** The sections; each `ListSection<T>` hands its `content` its own `args` (since 1.4.0). */
    readonly sections: readonly AnyListSection[];
    readonly title?: string | undefined;
    /** The chevron (`screen`), or Done, Back, Escape and the Back gesture alike (`page`). */
    readonly onback?: (() => void) | undefined;
    /** The way back's name; the shell's own "Back" (or, in the `done` dress, "Done") by default. */
    readonly backLabel?: string | undefined;
    readonly lead?: Snippet | undefined;
    readonly search?: Snippet | undefined;
    /** The id of the section the search follows; the search is first by default. */
    readonly searchAfter?: string | undefined;
    /**
     * `screen` (default): in place. `page`: a `FullPage` over the column,
     * which needs `title` and `onback` (`ListPagePageProps` checks it at
     * compile time). Since 1.3.0.
     */
    readonly frame?: Frame;
    /** Pad the titled in-place header's top by `env(safe-area-inset-top)`. Since 1.3.0. */
    readonly safeArea?: boolean;
    /** `page` frame only: `FullPage`'s `open` (default `true`). Since 1.3.0. */
    readonly open?: boolean;
    /** `page` frame only: `FullPage`'s dress (default `done`). Since 1.3.0. */
    readonly dress?: PageDress;
    /** `page` frame only: `FullPage`'s `wide`. Since 1.3.0. */
    readonly wide?: boolean;
    readonly testid?: string | undefined;
    readonly testids?: { readonly back?: string; readonly done?: string } | undefined;
  }

  const {
    sections,
    title,
    onback,
    backLabel,
    lead,
    search,
    searchAfter,
    frame = 'screen',
    safeArea = false,
    open = true,
    dress = 'done',
    wide = false,
    testid = 'ui-list-page',
    testids,
  }: Props = $props();

  // A page needs its name and its way out; without either it is drawn in
  // place (as 1.2.0 drew it), and the game is told once why.
  const asPage = $derived(frame === 'page' && title !== undefined && onback !== undefined);
  let warned = false;
  $effect(() => {
    if (frame !== 'page' || asPage || warned) return;
    warned = true;
    console.warn('ListPage: frame "page" needs a title and an onback; drawn in place instead.');
  });

  const pageTestids = $derived({
    ...(testids?.done !== undefined ? { done: testids.done } : {}),
    ...(testids?.back !== undefined ? { back: testids.back } : {}),
  });

  /** How many sections go above the search. */
  const searchIndex = $derived.by(() => {
    if (searchAfter === undefined) return 0;
    const at = sections.findIndex((section) => section.id === searchAfter);
    return at === -1 ? 0 : at + 1;
  });

  const uid = $props.id();
</script>

{#snippet sectionBlock(section: AnyListSection)}
  {@const labelId = `${uid}-${section.id}`}
  <section
    class="ui-list-section"
    aria-labelledby={section.label !== undefined ? labelId : undefined}
  >
    {#if section.label !== undefined}
      <SectionLabel as="h2" id={labelId}>{section.label}</SectionLabel>
    {/if}
    <div class="ui-list-group" data-testid={section.testid}>
      {@render section.content(section.args)}
    </div>
    {#if section.note !== undefined}
      <p class="ui-list-note">{section.note}</p>
    {/if}
  </section>
{/snippet}

{#snippet searchBlock()}
  {#if search !== undefined}
    <div class="ui-list-search">{@render search()}</div>
  {/if}
{/snippet}

{#snippet blocks()}
  {@render lead?.()}
  {#each sections.slice(0, searchIndex) as section (section.id)}
    {@render sectionBlock(section)}
  {/each}
  {@render searchBlock()}
  {#each sections.slice(searchIndex) as section (section.id)}
    {@render sectionBlock(section)}
  {/each}
{/snippet}

{#if asPage && title !== undefined}
  <FullPage
    {open}
    {title}
    onclose={() => onback?.()}
    {dress}
    {wide}
    leaveLabel={backLabel}
    {testid}
    testids={pageTestids}
  >
    <div class="ui-list-body ui-list-body--page">{@render blocks()}</div>
  </FullPage>
{:else}
  <div class="ui-list-page" class:ui-list-page--titled={title !== undefined} data-testid={testid}>
    {#if title !== undefined && onback !== undefined}
      <ScreenHeader {title} {onback} {backLabel} {safeArea} backTestid={testids?.back} />
    {:else if title !== undefined}
      <header class="ui-list-header"><h1 class="ui-list-title">{title}</h1></header>
    {/if}

    <div class="ui-list-body">{@render blocks()}</div>
  </div>
{/if}

<style>
  .ui-list-page {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background: var(--ui-ground);
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  /* A page of its own (with a header) fills the window. */
  .ui-list-page--titled {
    height: 100svh;
  }

  .ui-list-header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 3.5rem;
    padding: 0.4rem var(--ui-gutter);
    padding-top: calc(0.4rem + env(safe-area-inset-top));
  }

  .ui-list-title {
    margin: 0;
    font: var(--ui-text-heading);
    color: var(--ui-ink);
  }

  .ui-list-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-3);
    padding: var(--ui-space-2) var(--ui-gutter)
      calc(var(--ui-space-6) + env(safe-area-inset-bottom));
  }

  .ui-list-section {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .ui-list-group {
    display: flex;
    flex-direction: column;
  }

  /* The note shares the label's inset (`SectionLabel`'s 0.25rem). */
  .ui-list-note {
    margin: var(--ui-space-1) var(--ui-space-1) 0;
    font: var(--ui-text-caption);
    font-weight: 400;
    line-height: 1.4;
    color: var(--ui-ink-2);
  }

  .ui-list-search {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-2);
  }

  @media (min-width: 1024px) {
    .ui-list-body {
      padding: var(--ui-space-5) var(--ui-gutter-wide) var(--ui-space-6);
    }
  }

  /* In a FullPage: the page scrolls and insets its body itself (its dress's
     padding, the safe areas), so the list adds neither. */
  .ui-list-body.ui-list-body--page {
    flex: 1 0 auto;
    overflow: visible;
    padding: 0;
  }
</style>
